import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchTeamBySlug, TeamDetail } from '../lib/api';
import FixtureCard from '../components/fixtures/FixtureCard';
import EmptyState from '../components/ui/EmptyState';
import { HeroSkeleton, FixtureCardSkeleton } from '../components/ui/Skeleton';
import usePageTitle from '../hooks/usePageTitle';
import {
  ChevronRightIcon,
  CalendarIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

export default function TeamPage() {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<TeamDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  usePageTitle(data?.team.name);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetchTeamBySlug(slug)
      .then(setData)
      .catch(() => setError('Team not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div>
        <div className="h-4 w-48 mb-6" />
        <HeroSkeleton />
        <div className="h-5 w-36 mb-4 rounded bg-slate-700/50 animate-pulse" />
        <div className="grid gap-3">
          {Array.from({ length: 5 }).map((_, i) => <FixtureCardSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <EmptyState
        icon={<UserGroupIcon className="w-7 h-7" />}
        title="Team not found"
        description="We couldn't find the team you're looking for."
        actionLabel="Back to Home"
        actionTo="/"
      />
    );
  }

  const { team, fixtures } = data;

  return (
    <div>
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1.5 text-sm text-slate-500 mb-6">
        <Link to="/" className="hover:text-slate-300 transition-colors">Home</Link>
        <ChevronRightIcon className="w-3 h-3" />
        {team.league_slug && (
          <>
            <Link
              to={`/league/${team.league_slug}`}
              className="hover:text-slate-300 transition-colors"
            >
              {team.league_name}
            </Link>
            <ChevronRightIcon className="w-3 h-3" />
          </>
        )}
        <span className="text-slate-300">{team.name}</span>
      </nav>

      {/* Team hero */}
      <div className="card p-6 sm:p-8 mb-8">
        <div className="flex items-center gap-5">
          {team.logo ? (
            <img
              src={team.logo}
              alt={team.name}
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-slate-700 object-contain p-2"
            />
          ) : (
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-slate-700 flex items-center justify-center text-slate-300 text-3xl font-bold">
              {team.name.charAt(0)}
            </div>
          )}
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-100">{team.name}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-slate-400">
              {team.league_name && (
                <Link
                  to={`/league/${team.league_slug}`}
                  className="hover:text-emerald-400 transition-colors flex items-center gap-1.5"
                >
                  {team.league_logo && (
                    <img
                      src={team.league_logo}
                      alt={team.league_name}
                      className="w-4 h-4 rounded-xs object-contain"
                    />
                  )}
                  {team.league_name}
                </Link>
              )}
              <span className="flex items-center gap-1.5">
                <CalendarIcon className="w-4 h-4" />
                {fixtures.length} upcoming fixture{fixtures.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming fixtures */}
      <section>
        <h2 className="text-lg font-semibold text-slate-200 mb-4">Upcoming Fixtures</h2>
        {fixtures.length === 0 ? (
          <EmptyState
            icon={<CalendarIcon className="w-7 h-7" />}
            title="No upcoming fixtures"
            description="No upcoming fixtures found for this team."
          />
        ) : (
          <div className="grid gap-3">
            {fixtures.map((fixture) => (
              <FixtureCard
                key={fixture.match_id}
                fixture={fixture}
                showLeague={true}
                matchType={fixture.match_type}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
